import { CboRecordDto } from '@act/shared/data-transfer-objects';
import { useCallback } from 'react';
import { cboRecordRequests } from 'redux/requests/cboRecords.requests';
import { makeRequest } from 'redux/utils/apiRequest';
import {
  extractPlaceDisplayName,
  extractPlacePhoneForForm,
  extractPlaceWebsiteForForm,
  PLACE_DETAILS_FETCH_FIELDS,
} from '../googlePlacesNewAutocomplete.util';
import type { SelectedPlace } from '../placesSearch.types';
import {
  type GmpSelectAutocompleteEvent,
  type PlaceLike,
} from '../googlePlacesNewAutocomplete.types';
import { mapCboRecordToFormValues } from '../mapCboRecordToFormValues.util';
import type { CboResourceFormValues } from '../../forms/cboResourceForm/cboResourceForm.schema';
import { mapSelectedPlaceToFormValues } from '../mapSelectedPlaceToFormValues.util';

export interface UseResolvePlaceWithCacheResult {
  /**
   * Resolves a place prediction: server cache first, then Google `fetchFields` on miss.
   *
   * @param event - `gmp-select` event from PlaceAutocompleteElement.
   * @returns Partial form values for autofill.
   */
  resolveFromGmpSelect: (
    event: GmpSelectAutocompleteEvent
  ) => Promise<Partial<CboResourceFormValues>>;
  /**
   * Looks up a cached record by place id only (no Google Details call).
   *
   * @param placeId - Google place id.
   * @returns Cached DTO or null.
   */
  fetchCachedByPlaceId: (placeId: string) => Promise<CboRecordDto | null>;
}

/**
 * Combines CBO cache API lookup with optional Google Place Details fetch.
 *
 * @returns Resolver helpers for Places autocomplete.
 */
export function useResolvePlaceWithCache(): UseResolvePlaceWithCacheResult {
  const fetchCachedByPlaceId = useCallback(
    async (placeId: string): Promise<CboRecordDto | null> => {
      const record = await makeRequest(cboRecordRequests.getCboRecordByPlaceId, {
        params: { placeId },
      });
      return record ?? null;
    },
    []
  );

  const resolveFromGmpSelect = useCallback(
    async (
      event: GmpSelectAutocompleteEvent
    ): Promise<Partial<CboResourceFormValues>> => {
      const place: PlaceLike = event.placePrediction.toPlace();
      const placeId =
        event.placePrediction.placeId ?? place.id ?? undefined;

      if (placeId) {
        const cached = await fetchCachedByPlaceId(placeId);
        if (cached) {
          return mapCboRecordToFormValues(cached);
        }
      }

      await place.fetchFields({
        fields: [...PLACE_DETAILS_FETCH_FIELDS],
      });

      const resolvedId = place.id ?? placeId ?? '';
      if (!resolvedId) {
        return {};
      }

      const selected: SelectedPlace = {
        placeId: resolvedId,
        name: extractPlaceDisplayName(place.displayName),
        formattedAddress: place.formattedAddress ?? '',
        types: place.types ?? [],
        lat: place.location?.lat() ?? null,
        lng: place.location?.lng() ?? null,
        phoneNumber: extractPlacePhoneForForm(place.nationalPhoneNumber),
        websiteUrl: extractPlaceWebsiteForForm(place.websiteURI),
      };

      return mapSelectedPlaceToFormValues(selected);
    },
    [fetchCachedByPlaceId]
  );

  return { resolveFromGmpSelect, fetchCachedByPlaceId };
}
